import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { ErrorHandlerService } from '@ngx-stoui/error-handler';
import { Observable, catchError, from, mergeMap, throwError } from 'rxjs';
import { AppInsightsService } from '../services/app-insights.service';

// Context token to skip error dialog
export const SKIP_ERROR_DIALOG = new HttpContextToken<boolean>(() => false);

/**
 * Creates an HttpContext that skips the error dialog for HTTP requests.
 * Use this when you want to handle errors manually without showing the automatic error dialog.
 *
 * @example
 * ```typescript
 * // In a service
 * getData() {
 *   return this.http.get('/api/data', {
 *     context: skipErrorDialog()
 *   }).pipe(
 *     catchError(error => {
 *       // Handle error manually
 *       return of(null);
 *     })
 *   );
 * }
 * ```
 */
export function skipErrorDialog(): HttpContext {
  return new HttpContext().set(SKIP_ERROR_DIALOG, true);
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly msal = inject(MsalService);
  private clearingSession: Promise<void> | null = null;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const url = new URL(req.url, window.location.origin);
        const protectedApi =
          url.origin === window.location.origin &&
          /^\/(?:ct|lngct|lpgct|ctintegration|dummy\/validation)(?:\/|$)/.test(url.pathname);
        if (error.status === 401 && protectedApi) {
          return from(this.clearSession()).pipe(mergeMap(() => this.reportError(req, error)));
        }
        return this.reportError(req, error);
      }),
    );
  }

  private clearSession(): Promise<void> {
    if (!this.clearingSession) {
      this.msal.instance.setActiveAccount(null);
      this.clearingSession = this.msal.instance
        .clearCache()
        .catch((error: unknown) => {
          const failure = error instanceof Error ? error : new Error(String(error));
          console.error('Failed to clear the rejected authentication session', failure);
          this.appInsightsService.logException(failure, 3);
          this.errorHandlerService.handler(
            new HttpErrorResponse({ error: failure, statusText: 'Failed to clear authentication session' }),
          );
        })
        .finally(() => {
          this.clearingSession = null;
        });
    }
    return this.clearingSession;
  }

  private reportError(req: HttpRequest<unknown>, error: HttpErrorResponse): Observable<never> {
    if (!req.context.get(SKIP_ERROR_DIALOG)) {
      this.errorHandlerService.handler(error);
    }
    this.appInsightsService.logException(error, 3);
    return throwError(() => error);
  }
}
